import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Contact from "@/components/Contact";
import HowItWorks from "@/components/HowItWorks";
import ROICalculator from "@/components/ROICalculator";
import { scrollToId } from "@/lib/scroll";
import { salesPage } from "@/lib/home-content";

function BookButton({ children = "Book a call", testId = "sales-book", className = "" }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={() => scrollToId("#book")}
            className={`group inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(43,80,255,0.38)] transition-transform hover:-translate-y-0.5 ${className}`}
        >
            {children}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
    );
}

export default function SalesServicePage() {
    const location = useLocation();

    useEffect(() => {
        if (location.hash) scrollToId(location.hash);
    }, [location.hash]);

    return (
        <main data-testid="sales-service-page" className="bg-white text-[#0a0a0b]">
            <section className="bg-lavender px-5 pb-16 pt-36 sm:pb-20 sm:pt-44">
                <div className="mx-auto max-w-5xl">
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--brand)] ring-1 ring-black/5">
                        {salesPage.kicker}
                    </span>
                    <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-tight text-[#0a0a0b] sm:text-5xl">
                        {salesPage.title}
                    </h1>
                    <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#5b5b66] sm:text-lg">
                        {salesPage.subtitle}
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <BookButton testId="sales-hero-book" />
                        <button
                            type="button"
                            onClick={() => scrollToId("#roi")}
                            className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#0a0a0b] ring-1 ring-black/10 hover:text-[var(--brand)]"
                        >
                            See the revenue upside
                        </button>
                    </div>
                    <p className="mt-4 text-sm text-[#6b6b75]">30 minutes. You leave with the leak and a price.</p>
                </div>
            </section>

            <section className="px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-5xl">
                    <h2 className="max-w-2xl font-display text-3xl font-extrabold leading-tight sm:text-4xl">
                        Three ways we work on a sales floor.
                    </h2>
                    <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#5b5b66]">
                        Pick the one that matches the problem. Most engagements start with one and add the others once
                        the pipeline is visible.
                    </p>
                    <div className="mt-8 grid gap-4 md:grid-cols-3">
                        {salesPage.offers.map((offer) => (
                            <article key={offer.no} className="flex flex-col rounded-3xl bg-[#f6f7fb] p-6 ring-1 ring-black/5">
                                <span className="font-display text-sm font-extrabold text-[var(--brand)]">{offer.no}</span>
                                <h3 className="mt-3 font-display text-xl font-bold">{offer.title}</h3>
                                <p className="mt-2 flex-1 text-sm leading-relaxed text-[#5b5b66]">{offer.body}</p>
                                <BookButton testId={`sales-offer-${offer.no}`} className="mt-6 w-fit" />
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <HowItWorks />

            <section className="px-5 pb-4">
                <div className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-5 rounded-3xl bg-[#0a0a0b] px-6 py-8 text-white sm:flex-row sm:items-center sm:px-8">
                    <div>
                        <h2 className="font-display text-2xl font-extrabold">{salesPage.call.title}</h2>
                        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/70">{salesPage.call.body}</p>
                    </div>
                    <BookButton testId="sales-mid-book" className="shrink-0" />
                </div>
            </section>

            <ROICalculator />

            <section className="bg-[#f6f7fb] px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-3xl">
                    <h2 className="font-display text-3xl font-extrabold">Questions before the call</h2>
                    <div className="mt-6 space-y-3">
                        {salesPage.faq.map((item) => (
                            <div key={item.q} className="rounded-2xl bg-white p-5 ring-1 ring-black/5">
                                <p className="font-semibold">{item.q}</p>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{item.a}</p>
                            </div>
                        ))}
                    </div>
                    <ul className="mt-8 space-y-2 text-sm text-[#3a3a44]">
                        {["The call is with the founder, not a setter.", "You keep the CRM and the process we build.", "Month-to-month after the rebuild. No lock-in."].map(
                            (point) => (
                                <li key={point} className="flex items-start gap-2">
                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand)]" />
                                    {point}
                                </li>
                            )
                        )}
                    </ul>
                </div>
            </section>

            <Contact
                sectionId="book"
                copy={salesPage.form}
                submitLabel="Book the diagnostic"
                mailSubject="Sales diagnostic — SalesCan"
                companyPlaceholder="Northline — 8-person sales team"
                goalLabel="What do you need on the pipeline?"
            />
        </main>
    );
}
